import React from 'react';
import styled from 'styled-components';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { scaleLinear } from 'd3-scale';
import { max } from 'd3-array';
import { animated, Spring } from 'react-spring/renderprops';
import { translate, getRScale } from '~/utils';
import {
  getCircleColor,
  getFillOpacity,
  getStrokeOpacity,
  getStrokeColor,
  getStrokeWidth,
} from './chart-utils';

import AxisLeft from './AxisLeft';
import AxisBottom from './AxisBottom';
import Grid from './Grid';
import Tooltip from './Tooltip';
import Annotations from './Annotations';
import AxisLabels from './AxisLabels';

const ChartWrapper = styled.div`
  margin-bottom: 10px;
  position: relative;
`;

const Chart = styled.svg`
  display: block;
`;

const Circle = styled(animated.circle)`
  fill: ${getCircleColor};
  fill-opacity: ${getFillOpacity};
  stroke: ${getStrokeColor};
  stroke-opacity: ${getStrokeOpacity};
  stroke-width: ${getStrokeWidth};
  pointer-events: ${(p) => (p.isHighlighted || p.isSelected ? 'all' : 'none')};
  transition: fill-opacity 0.2s, stroke-opacity 0.2s;

  &:hover {
    stroke-width: 3;
  }
`;

const ChartDescription = styled.g`
  font-size: 12px;
  color: #777;
`;

const margin = {
  top: 30,
  right: 10,
  bottom: 50,
  left: 50,
};

const Scatterplot = ({ width, showFullXScale = false, isRelView = false }) => {
  const height = Math.max(width * 0.8, 380);
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const data = useStoreState((state) => state.app.chartData);

  const setTooltipData = useStoreActions(
    (actions) => actions.tooltip.setTooltipData
  );
  const setValue = useStoreActions((actions) => actions.app.setValue);

  const xMax = showFullXScale ? max(data, (d) => d.x) : 200;

  const xScale = scaleLinear()
    .domain([xMax, 0])
    .range([0, innerWidth])
    .clamp(true)
    .nice();
  const yScale = scaleLinear()
    .domain([1500, 6150])
    .range([innerHeight, 0])
    .clamp(true);
  const rScale = getRScale();

  const handleCircleMouseLeave = () => setTooltipData(null);

  const circles = data.map((d) => {
    const x = xScale(d.x);
    const y = yScale(d.y);
    const r = rScale(d.r);

    const handleClick = (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      setValue(d.value);
    };

    const handleMouseEnter = () => setTooltipData(d);

    return (
      <Spring key={d.value} native to={{ x, y, r }}>
        {(props) => (
          <Circle
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleCircleMouseLeave}
            onClick={handleClick}
            isHighlighted={d.highlight}
            isSelected={d.selected}
            cx={props.x}
            cy={props.y}
            r={props.r}
            qualification={d.qualification}
            key={d.value}
          />
        )}
      </Spring>
    );
  });

  const xTicks = xScale.ticks(4);
  const yTicks = [2000, 3000, 4000, 5000, 6150];
  const chartDescriptionXPos = innerWidth / 2;

  return (
    <>
      <ChartWrapper>
        <Chart width={width} height={height}>
          <g transform={translate(margin.left, margin.top)}>
            <Grid
              xTicks={xTicks}
              yTicks={yTicks}
              xScale={xScale}
              yScale={yScale}
              width={innerWidth}
              height={innerHeight}
            />
            <AxisBottom
              ticks={xTicks}
              scale={xScale}
              transform={translate(0, innerHeight)}
              margin={margin}
              isRelView={isRelView}
            />
            <AxisLeft ticks={yTicks} scale={yScale} margin={margin} />
            {circles}
            <Annotations
              xScale={xScale}
              yScale={yScale}
              rScale={rScale}
              data={data}
            />

            <ChartDescription
              transform={translate(chartDescriptionXPos, innerHeight + 24)}
            >
              <text x={-4} textAnchor="end">
                ← geringer
              </text>
              <text x={4} textAnchor="start">
                höher →
              </text>
            </ChartDescription>
          </g>
        </Chart>
        <AxisLabels margin={margin} />
      </ChartWrapper>
      <Tooltip
        margin={margin}
        xScale={xScale}
        yScale={yScale}
        width={width}
        height={height}
      />
    </>
  );
};

export default Scatterplot;
